import type { LessonInput } from "@/generated/graphql";
import { THUMBNAIL } from "@/lib/constants/shared";
import { useAddLesson } from "@/lib/hooks/lessons/useAddLesson";
import { useEditLesson } from "@/lib/hooks/lessons/useEditLesson";
import { useSchools } from "@/lib/hooks/schools/useSchool";
import { scrollToItem } from "@/lib/utils/scrollToItem";
import type { LessonFields } from "@/types/Lesson";
import { Button, FormField, LoadMoreButton, Modal, SearchBar } from "@ai-teach/ui-web";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { ImageUpload } from "../ImageUpload";

export interface School {
  id: string;
  name: string;
}

export interface AddLessonModalProps {
  isOpen: boolean;
  onClose: () => void;
  parentId: string | null;
  lesson?: LessonFields; // Optional lesson prop for edit mode
}

export function AddLessonModal({ isOpen, onClose, parentId, lesson }: AddLessonModalProps) {
  const isEditMode = !!lesson;

  const [formData, setFormData] = useState<LessonInput>({
    imageId: lesson?.imageId || undefined,
    isDeleted: lesson?.isDeleted || undefined,
    isPublic: lesson?.isPublic ?? true,
    isTrash: lesson?.isTrash || undefined,
    name: lesson?.name || "",
    parentId: lesson?.parentId || parentId || null,
    schoolIds: lesson?.schoolIds || [],
  });

  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);

  const [uiState, setUiState] = useState({
    schoolSearch: "",
    showSchoolSelector: false,
  });

  const {
    schools,
    loading: schoolsLoading,
    loadMore: loadMoreSchools,
    hasMore: hasMoreSchools,
  } = useSchools(uiState.schoolSearch);

  const [fieldErrors, setFieldErrors] = useState({
    name: false,
    schools: false,
  });

  // Filter schools based on search
  const filteredSchools = schools.filter((school) =>
    school.name.toLowerCase().includes(uiState.schoolSearch.toLowerCase())
  );

  const selectedSchoolObjects = schools.filter((school) => formData.schoolIds?.includes(school.id));

  const handleSchoolToggle = (schoolId: string) => {
    setFormData((prev) => ({
      ...prev,
      schoolIds: prev.schoolIds?.includes(schoolId)
        ? prev.schoolIds.filter((id) => id !== schoolId)
        : [...(prev.schoolIds || []), schoolId],
    }));
    if (fieldErrors.schools) {
      setFieldErrors((prev) => ({ ...prev, schools: false }));
    }
  };

  // Sync form state when lesson prop changes (edit mode)
  useEffect(() => {
    if (lesson) {
      setFormData({
        imageId: lesson.imageId || undefined,
        isDeleted: lesson.isDeleted || undefined,
        isPublic: lesson.isPublic ?? true,
        isTrash: lesson.isTrash || undefined,
        name: lesson.name || "",
        parentId: lesson.parentId || null,
        schoolIds: lesson.schoolIds || [],
      });
    }
  }, [lesson]);

  const handleClose = () => {
    // Reset form only if not in edit mode
    if (!isEditMode) {
      setFormData({
        imageId: undefined,
        isDeleted: undefined,
        isPublic: true,
        isTrash: undefined,
        name: "",
        parentId: undefined,
        schoolIds: [],
        slideIds: undefined,
      });
    }
    onClose();
  };

  const { addLesson, loading: addLessonLoading } = useAddLesson();
  const { editLesson, loading: editLessonLoading } = useEditLesson();

  const isLoading = editLessonLoading || addLessonLoading;

  const handleSubmit = async () => {
    // Validate required fields
    const errors = {
      name: !formData.name.trim(),
      schools: !formData.isPublic && (!formData.schoolIds || formData.schoolIds.length === 0),
    };

    // Check if any errors exist
    if (Object.values(errors).some((error) => error)) {
      return;
    }

    const lessonData = {
      ...formData,
      name: formData.name.trim(),
    };

    if (isEditMode && lesson) {
      // Send file directly to backend - backend handles upload/deletion/cleanup
      await editLesson(
        lesson.id,
        lessonData,
        selectedImageFile,
        formData.imageId === null // removeImage flag
      );
    } else {
      // Send file directly to backend - backend handles upload/cleanup
      const newLessons = await addLesson(lessonData, selectedImageFile);
      if (newLessons?.[0]) scrollToItem("lesson", newLessons[0].id);
    }
    handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? "Edit Lesson" : "Add Lesson"}
      size="lg"
    >
      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-4 mb-4">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        {isEditMode ? (
          <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? "Saving..." : "Save"}
          </Button>
        ) : (
          <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? "Adding..." : "Add"}
          </Button>
        )}
      </div>
      <div className="space-y-6">
        {/* Access Control Section */}
        <div>
          <div
            className={`block text-sm font-medium mb-3 ${fieldErrors.schools ? "text-red-500" : "text-secondary-200"}`}
          >
            <span className="text-red-500">*</span>
            <span className="font-bold"> Access Control:</span> This access control applies only to
            schools that have access to this project. If a school does not have access, it will not
            appear below.
          </div>
          <div className="space-y-2">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="accessControl"
                value="all"
                checked={formData.isPublic}
                onChange={() => {
                  setFormData((prev) => ({ ...prev, isPublic: true, schoolIds: [] }));
                  if (fieldErrors.schools) {
                    setFieldErrors((prev) => ({ ...prev, schools: false }));
                  }
                }}
                className="sr-only"
              />
              {formData.isPublic ? (
                <MdCheckBox className="w-5 h-5 text-success-500" />
              ) : (
                <MdCheckBoxOutlineBlank className="w-5 h-5 text-gray-400" />
              )}
              <span className="text-sm text-secondary-200">All schools</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="accessControl"
                value="selected"
                checked={!formData.isPublic}
                onChange={() => setFormData((prev) => ({ ...prev, isPublic: false }))}
                className="sr-only"
              />
              {!formData.isPublic ? (
                <MdCheckBox className="w-5 h-5 text-success-500" />
              ) : (
                <MdCheckBoxOutlineBlank className="w-5 h-5 text-gray-400" />
              )}
              <span className="text-sm text-secondary-200">Selected schools</span>
            </label>
          </div>
        </div>

        {/* School Selection (shown when "Selected schools" is chosen) */}
        {!formData.isPublic && (
          <div
            className={`border rounded-lg p-4 space-y-4 ${fieldErrors.schools ? "border-red-500" : "border-gray-800"}`}
          >
            <div className="flex items-center justify-between">
              <div className="block label">
                <span className="text-red-500">*</span> Select the schools
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setUiState((prev) => ({
                    ...prev,
                    showSchoolSelector: !prev.showSchoolSelector,
                  }))
                }
              >
                {uiState.showSchoolSelector ? "Hide" : "Show"} selector
              </Button>
            </div>

            {/* Selected Schools Display */}
            {selectedSchoolObjects.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {selectedSchoolObjects.map((school) => (
                  <label key={school.id} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked
                      onChange={() => handleSchoolToggle(school.id)}
                      className="sr-only"
                    />
                    <div>
                      <MdCheckBox className="w-5 h-5 text-success-500" />
                    </div>
                    <span className="text-sm text-secondary-200">{school.name}</span>
                  </label>
                ))}
              </div>
            )}

            {/* School Selector */}
            {uiState.showSchoolSelector && (
              <div className="space-y-3">
                <SearchBar
                  value={uiState.schoolSearch}
                  onChange={(value: string) =>
                    setUiState((prev) => ({ ...prev, schoolSearch: value }))
                  }
                  placeholder="Search for school"
                  className="w-full"
                />

                <div className="space-y-2 max-h-60 overflow-y-auto gradient-scrollbar">
                  <p className="caption font-medium uppercase">School</p>
                  <div className="grid grid-cols-3 gap-2">
                    {filteredSchools.map((school) => (
                      <label key={school.id} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.schoolIds?.includes(school.id) || false}
                          onChange={() => handleSchoolToggle(school.id)}
                          className="sr-only"
                        />
                        <div>
                          {formData.schoolIds?.includes(school.id) ? (
                            <MdCheckBox className="w-5 h-5 text-success-500" />
                          ) : (
                            <MdCheckBoxOutlineBlank className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                        <span className="text-sm text-secondary-200 leading-tight">
                          {school.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {true && (
                  <LoadMoreButton
                    onClick={loadMoreSchools}
                    hasMore={hasMoreSchools}
                    loading={schoolsLoading}
                    defaultText="Load more"
                  />
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex gap-6">
          {/* Image Upload - Left Side */}
          <div className="flex-1">
            <label htmlFor="image" className="block label mb-2">
              Thumbnail Image
            </label>
            <ImageUpload
              onFileSelect={setSelectedImageFile}
              onRemove={() => {
                setSelectedImageFile(null);
                setFormData((prev) => ({ ...prev, imageId: null }));
              }}
              currentImageUrl={lesson?.imageUrl ?? null}
              aspectRatio={THUMBNAIL.lessonAspectRatio}
            />
          </div>

          {/* Form Fields - Right Side */}
          <div className="flex-1 space-y-4">
            {/* Name Field */}
            <FormField
              id="name"
              name="name"
              label="Name"
              type="text"
              value={formData.name}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, name: e.target.value }));
                if (fieldErrors.name) {
                  setFieldErrors((prev) => ({ ...prev, name: false }));
                }
              }}
              required
              placeholder="Enter lesson name"
              error={fieldErrors.name ? "Lesson name is required" : undefined}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
